import { useLocation, useHistory } from 'react-router';
import { useEffect, useRef, useState } from 'react';

type ContentTab = 'audio' | 'video' | 'resources' | 'collections';

export interface SharedFilterValues {
  tagGroupIds: number[];
  tagIds: number[];
  domainId: number | null;
  clientId: number | null;
}

export interface StoriesFilterValues extends SharedFilterValues {
  showTestUsers: boolean;
}

export interface ResourcesFilterValues extends SharedFilterValues {
  statuses: ('live' | 'hidden')[];
}

export interface CollectionsFilterValues extends SharedFilterValues {
  verticalIds: number[];
}

const parseIdsFromUrl = (param: string | null): number[] => {
  if (!param) return [];
  try {
    return param
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !isNaN(id));
  } catch {
    return [];
  }
};

const VALID_STATUSES = new Set<'live' | 'hidden'>(['live', 'hidden']);

const parseSharedFilters = (searchParams: URLSearchParams): SharedFilterValues => {
  const tagGroupIds = parseIdsFromUrl(searchParams.get('tag_group_ids'));
  const tagGroupsLegacy = searchParams.get('tag_groups');
  const domainIds = parseIdsFromUrl(searchParams.get('domain_ids'));
  const domainIdParam = searchParams.get('domain_id');
  const clientIds = parseIdsFromUrl(searchParams.get('client_ids'));
  const clientIdParam = searchParams.get('client_id');
  const domainId = domainIds[0] ?? (domainIdParam ? parseInt(domainIdParam, 10) : null);
  const clientId = clientIds[0] ?? (clientIdParam ? parseInt(clientIdParam, 10) : null);
  return {
    tagGroupIds:
      tagGroupIds.length > 0
        ? tagGroupIds
        : tagGroupsLegacy
          ? [parseInt(tagGroupsLegacy, 10)].filter((n) => !isNaN(n))
          : [],
    tagIds: parseIdsFromUrl(searchParams.get('tag_ids')),
    domainId: domainId && !isNaN(domainId) ? domainId : null,
    clientId: clientId && !isNaN(clientId) ? clientId : null,
  };
};

interface ContentLibraryUrlState {
  tab: ContentTab;
  audioPage: number;
  videoPage: number;
  resourcesPage: number;
  collectionsPage: number;
  searchTerms: Record<ContentTab, string>;
  sharedFilters: SharedFilterValues;
  storiesFilters: { showTestUsers: boolean };
  resourcesFilters: { statuses: ('live' | 'hidden')[] };
  collectionsFilters: { verticalIds: number[] };
}

export const useContentLibraryUrlState = () => {
  const location = useLocation();
  const history = useHistory();
  const isUpdatingFromUrl = useRef(false);

  const parseQueryParams = (): ContentLibraryUrlState => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const validTabs: ContentTab[] = ['audio', 'video', 'resources', 'collections'];

    const statusesParam = searchParams.get('resources_status');
    const statuses = statusesParam
      ? statusesParam
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter((s): s is 'live' | 'hidden' => VALID_STATUSES.has(s as 'live' | 'hidden'))
      : [];

    return {
      tab: (tabParam && validTabs.includes(tabParam as ContentTab) ? tabParam : 'audio') as ContentTab,
      audioPage: parseInt(searchParams.get('audio_page') || '1', 10),
      videoPage: parseInt(searchParams.get('video_page') || '1', 10),
      resourcesPage: parseInt(searchParams.get('resources_page') || '1', 10),
      collectionsPage: parseInt(searchParams.get('collections_page') || '1', 10),
      searchTerms: {
        audio: searchParams.get('audio_search') || '',
        video: searchParams.get('video_search') || '',
        resources: searchParams.get('resources_search') || '',
        collections: searchParams.get('collections_search') || '',
      },
      sharedFilters: parseSharedFilters(searchParams),
      storiesFilters: { showTestUsers: searchParams.get('show_test_users') === 'true' },
      resourcesFilters: { statuses },
      collectionsFilters: { verticalIds: parseIdsFromUrl(searchParams.get('vertical_ids')) },
    };
  };

  const initialParams = parseQueryParams();
  const [activeTab, setActiveTab] = useState<ContentTab>(initialParams.tab);
  const [audioPage, setAudioPage] = useState(initialParams.audioPage);
  const [videoPage, setVideoPage] = useState(initialParams.videoPage);
  const [resourcesPage, setResourcesPage] = useState(initialParams.resourcesPage);
  const [collectionsPage, setCollectionsPage] = useState(initialParams.collectionsPage);
  const [searchTerms, setSearchTerms] = useState(initialParams.searchTerms);
  const [sharedFilters, setSharedFilters] = useState<SharedFilterValues>(initialParams.sharedFilters);
  const [storiesFilters, setStoriesFilters] = useState(initialParams.storiesFilters);
  const [resourcesFilters, setResourcesFilters] = useState(initialParams.resourcesFilters);
  const [collectionsFilters, setCollectionsFilters] = useState(initialParams.collectionsFilters);

  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }

    const params = parseQueryParams();
    if (params.tab !== activeTab) setActiveTab(params.tab);
    if (params.audioPage !== audioPage && activeTab === 'audio') setAudioPage(params.audioPage);
    if (params.videoPage !== videoPage && activeTab === 'video') setVideoPage(params.videoPage);
    if (params.resourcesPage !== resourcesPage && activeTab === 'resources') setResourcesPage(params.resourcesPage);
    if (params.collectionsPage !== collectionsPage && activeTab === 'collections')
      setCollectionsPage(params.collectionsPage);
    if (JSON.stringify(params.searchTerms) !== JSON.stringify(searchTerms)) setSearchTerms(params.searchTerms);
    if (JSON.stringify(params.sharedFilters) !== JSON.stringify(sharedFilters)) setSharedFilters(params.sharedFilters);
    if (JSON.stringify(params.storiesFilters) !== JSON.stringify(storiesFilters))
      setStoriesFilters(params.storiesFilters);
    if (JSON.stringify(params.resourcesFilters) !== JSON.stringify(resourcesFilters))
      setResourcesFilters(params.resourcesFilters);
    if (JSON.stringify(params.collectionsFilters) !== JSON.stringify(collectionsFilters))
      setCollectionsFilters(params.collectionsFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramMap: Record<string, string | undefined> = {
      tab: activeTab,
      audio_page: audioPage > 1 ? audioPage.toString() : undefined,
      video_page: videoPage > 1 ? videoPage.toString() : undefined,
      resources_page: resourcesPage > 1 ? resourcesPage.toString() : undefined,
      collections_page: collectionsPage > 1 ? collectionsPage.toString() : undefined,
      audio_search: searchTerms.audio || undefined,
      video_search: searchTerms.video || undefined,
      resources_search: searchTerms.resources || undefined,
      collections_search: searchTerms.collections || undefined,
      tag_group_ids:
        sharedFilters.tagGroupIds.length > 0
          ? [...sharedFilters.tagGroupIds].sort((a, b) => a - b).join(',')
          : undefined,
      tag_ids: sharedFilters.tagIds.length > 0 ? [...sharedFilters.tagIds].sort((a, b) => a - b).join(',') : undefined,
      domain_id: sharedFilters.domainId != null ? String(sharedFilters.domainId) : undefined,
      client_id: sharedFilters.clientId != null ? String(sharedFilters.clientId) : undefined,
      show_test_users: storiesFilters.showTestUsers ? 'true' : undefined,
      resources_status: resourcesFilters.statuses.length > 0 ? resourcesFilters.statuses.join(',') : undefined,
      vertical_ids:
        collectionsFilters.verticalIds.length > 0
          ? [...collectionsFilters.verticalIds].sort((a, b) => a - b).join(',')
          : undefined,
    };

    Object.entries(paramMap).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });

    isUpdatingFromUrl.current = true;
    history.replace({ ...location, search: searchParams.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    audioPage,
    videoPage,
    resourcesPage,
    collectionsPage,
    searchTerms,
    sharedFilters,
    storiesFilters,
    resourcesFilters,
    collectionsFilters,
  ]);

  return {
    activeTab,
    setActiveTab,
    audioPage,
    setAudioPage,
    videoPage,
    setVideoPage,
    resourcesPage,
    setResourcesPage,
    collectionsPage,
    setCollectionsPage,
    searchTerms,
    setSearchTerms,
    sharedFilters,
    setSharedFilters,
    storiesFilters,
    setStoriesFilters,
    resourcesFilters,
    setResourcesFilters,
    collectionsFilters,
    setCollectionsFilters,
  };
};
